import type { DashboardSnapshot, KPI, Lead, Persona } from '../types';

const roleCopy = {
  manager: { eyebrow: 'Visao gerencial', title: 'Operacao em um so lugar', intro: 'Acompanhe a saude comercial e operacional sem perder o contexto.' },
  new_sales: { eyebrow: 'Minha carteira', title: 'Seu dia comercial, sem ruido', intro: 'Priorize oportunidades e acompanhe apenas os seus resultados.' },
  renewals: { eyebrow: 'Minha agenda', title: 'Renovacoes sob controle', intro: 'Antecipe vencimentos e concentre-se nas pendencias da sua carteira.' },
  sdr: { eyebrow: 'Minha fila', title: 'Contato rapido, passagem clara', intro: 'Acompanhe seu ritmo de qualificacao e os proximos contatos.' },
  post_sales: { eyebrow: 'Minha jornada', title: 'Relacionamento que continua', intro: 'Cuide das boas-vindas, pendencias e retornos do pos-venda.' },
} as const;

export function dashboardCopy(persona: Persona) {
  return roleCopy[persona.role];
}

export function buildKpis(persona: Persona, ownLeads: Lead[], live?: DashboardSnapshot | null, demoMode = true): KPI[] {
  if (live) return [
    { label: 'Leads hoje', value: String(live.leads_today), delta: live.scope === 'organization' ? 'Visao autorizada da operacao' : 'Somente seus registros', tone: 'teal', hint: 'Dados protegidos por RLS' },
    { label: 'Em andamento', value: String(live.active_leads), delta: 'Atualizado pelo banco', tone: 'amber', hint: 'Sem comparacao entre colegas' },
    { label: 'Concluidos', value: String(live.won_leads), delta: 'Periodo atual', tone: 'navy', hint: 'Escopo do seu perfil' },
    { label: 'Escopo de acesso', value: live.scope === 'organization' ? 'Gestao' : 'Pessoal', delta: 'Menor privilegio', tone: 'coral', hint: 'Aplicado no banco e na interface' },
  ];
  if (!demoMode) {
    const today = new Date().toISOString().slice(0, 10);
    return [
      { label: 'Leads hoje', value: String(ownLeads.filter((lead) => lead.receivedAt.startsWith(today)).length), delta: 'Dados carregados do banco', tone: 'teal', hint: 'Escopo protegido por RLS' },
      { label: 'Em andamento', value: String(ownLeads.filter((lead) => !['ganho', 'perdido'].includes(lead.status)).length), delta: 'Sem dados demonstrativos', tone: 'amber', hint: 'Somente registros autorizados' },
      { label: 'Concluidos', value: String(ownLeads.filter((lead) => lead.status === 'ganho').length), delta: 'Dados carregados do banco', tone: 'navy', hint: 'Escopo do seu perfil' },
      { label: 'Escopo de acesso', value: persona.role === 'manager' ? 'Gestao' : 'Pessoal', delta: 'Menor privilegio', tone: 'coral', hint: 'Aplicado no banco e na interface' },
    ];
  }
  if (persona.role === 'manager') {
    return [
      { label: 'Leads capturados', value: '52', delta: '+12% na semana', tone: 'teal', hint: 'Fontes validadas e demo' },
      { label: 'Em atendimento', value: '18', delta: '4 prioritarios', tone: 'amber', hint: 'Fila operacional' },
      { label: 'Producao conciliada', value: 'R$ 48,2 mil', delta: '86% da meta', tone: 'navy', hint: 'Venda + emissao confirmada' },
      { label: 'Pendencias criticas', value: '3', delta: 'Requerem decisao', tone: 'coral', hint: 'Sem expor dados pessoais' },
    ];
  }
  if (persona.role === 'new_sales') {
    const proposals = ownLeads.filter((lead) => lead.status === 'proposta').length;
    return [
      { label: 'Meus leads hoje', value: String(ownLeads.filter((lead) => lead.receivedAt.startsWith('2026-10-16')).length), delta: 'Somente sua carteira', tone: 'teal', hint: 'Sem comparacao entre colegas' },
      { label: 'Minhas propostas', value: String(proposals), delta: 'Atualizado agora', tone: 'navy', hint: 'Oportunidades ativas' },
      { label: 'Aguardando retorno', value: '2', delta: '1 vence hoje', tone: 'amber', hint: 'Proximas acoes pessoais' },
      { label: 'Minha producao', value: 'R$ 12,4 mil', delta: 'Periodo atual', tone: 'coral', hint: 'Visivel apenas para voce e gestor' },
    ];
  }
  if (persona.role === 'renewals') return [
    { label: 'Vencem em 30 dias', value: '21', delta: 'Sua carteira', tone: 'teal', hint: 'Janela de renovacao' },
    { label: 'Cotacoes prontas', value: '9', delta: '3 hoje', tone: 'navy', hint: 'Prontas para contato' },
    { label: 'Sem retorno', value: '4', delta: 'Acao recomendada', tone: 'amber', hint: 'Fila pessoal' },
    { label: 'Renovadas', value: '82%', delta: 'Periodo atual', tone: 'coral', hint: 'Seu indicador pessoal' },
  ];
  if (persona.role === 'sdr') return [
    { label: 'Minha fila hoje', value: '17', delta: '5 novos', tone: 'teal', hint: 'Contatos atribuidos a voce' },
    { label: 'Qualificados', value: '11', delta: '64% da fila', tone: 'navy', hint: 'Passagens concluidas' },
    { label: 'Tempo de 1º contato', value: '6 min', delta: '-2 min na semana', tone: 'amber', hint: 'Mediana pessoal' },
    { label: 'Retornos agendados', value: '5', delta: '2 ate meio-dia', tone: 'coral', hint: 'Sua agenda' },
  ];
  return [
    { label: 'Boas-vindas hoje', value: '8', delta: '3 concluidas', tone: 'teal', hint: 'Sua fila' },
    { label: 'Pendencias abertas', value: '6', delta: '2 prioritarias', tone: 'amber', hint: 'Documentos e retornos' },
    { label: 'Retornos no prazo', value: '94%', delta: 'Periodo atual', tone: 'navy', hint: 'Indicador pessoal' },
    { label: 'NPS respondido', value: '12', delta: 'Ultimos 7 dias', tone: 'coral', hint: 'Sem expor respostas' },
  ];
}

